import { GDocTableData, IUnitLessonParams, ParsedGoogleDocType } from './types'
import {
  createActivity,
  createNoteNode,
  parseHtmlString,
} from '../nodeCreationHelpers'
import {
  createAnchor,
  createImgTag,
  PARAGRAPH_SPACING,
  wrapB,
} from '../html_utility'

export function getMetadataValueForKey<T>(
  valueKey: string,
  tableData: GDocTableData,
  transform: (data: string) => T,
) {
  return getTableValueForKey({
    tableName: 'document-metadata',
    valueKey,
    tableData,
    transform,
  })
}

export function getStorylineValueForKey<T>(
  valueKey: string,
  tableData: GDocTableData,
  transform: (data: string) => T,
) {
  return getTableValueForKey({
    tableName: '[storyline]',
    valueKey,
    tableData,
    transform,
  })
}

export function getMaterialMetadataForKey<T>(
  valueKey: string,
  tableData: GDocTableData,
  transform: (data: string) => T,
) {
  return getTableValueForKey({
    tableName: 'material-metadata',
    valueKey,
    tableData,
    transform,
  })
}

export function getWhereGoingForKey<T>(
  valueKey: 'where-going' | 'where-not-going',
  tableData: GDocTableData,
  transform: (data: string) => T,
) {
  return getTableValueForKey({
    tableName: '[where-we-are-going-and-not]',
    valueKey,
    tableData,
    transform,
  })
}

export function getTableValueForKey<T>({
  tableName,
  tableData,
  transform,
  valueKey,
}: {
  tableName: string
  valueKey: string
  tableData: GDocTableData
  transform: (data: string) => T
}) {
  const htmlData = tableData[tableName]?.[0][valueKey] ?? ''
  return transform(htmlData)
}

export function getAllTableValuesForKey<T>({
  tableName,
  tableData,
  transform,
  valueKey,
  options,
}: {
  tableName: string
  valueKey: string
  tableData: GDocTableData
  transform: (data: string) => T
  options?: {
    filtered?: boolean
    unique?: boolean
  }
}) {
  return tableData[tableName].reduce<T[]>((acc, data) => {
    const value = transform(data[valueKey])
    if (options?.filtered && !value) return acc
    if (options?.unique && acc.includes(value)) return acc
    return [...acc, value]
  }, [])
}

export function htmlTransform(htmlString: string) {
  return htmlString
}

export function unStyledHtml(htmlString: string) {
  const asElement = parseHtmlString(htmlString)
  removeAllClassAndStyles(asElement.body)
  return asElement.body.innerHTML
}

export function intTransform(htmlString: string) {
  return parseInt(stringTransform(htmlString), 10)
}

export function stringTransform(htmlString: string) {
  return parseHtmlString(htmlString).body.textContent?.trim() ?? ''
}

export function csvTransform(htmlString: string) {
  return stringTransform(htmlString)
    .split(',')
    .map((s) => s.trim())
}

export function removeAllClassAndStyles(
  elememt: HTMLElement,
  options?: { filterTags?: string[] },
) {
  elememt.querySelectorAll('*').forEach((e) => {
    if (options?.filterTags?.includes(e.tagName.toLowerCase())) {
      return
    }
    if (parseInt((e as HTMLElement).style.fontWeight ?? '0', 10) > 400) {
      e.innerHTML = wrapB(e.innerHTML)
    }
    e.removeAttribute('style')
    e.removeAttribute('class')
  })
}

export function isMaterialsFile({
  name,
  namePath,
}: {
  name: string
  namePath: string
}) {
  if (!namePath.includes('Materials')) return false
  const regex = /^[A-Z]{2}\.L\d{1,2}\.[A-Z0-9]{1,5}.*$/
  return regex.test(name)
}

function getMaterialIdSwap(
  materialId: string,
  { gDrive }: { gDrive: Record<string, ParsedGoogleDocType> },
) {
  const contentKey = materialId.replaceAll('.', '').toUpperCase()
  // In some cases the content key should have a 1 after it
  const tableContent = (gDrive[contentKey] ?? gDrive[contentKey + '1'])
    ?.tableContent
  if (!tableContent) return
  return getMaterialMetadataForKey('Title', tableContent, stringTransform)
}

function swapMaterialCodes(
  content: string,
  params: { gDrive: Record<string, ParsedGoogleDocType> },
) {
  const regex = /\[material:\s*([\w.]+)]/gim
  return content.replace(regex, (match, capture) => {
    return getMaterialIdSwap(capture, params) ?? match
  })
}

function swapVideoCodes(
  content: string,
  { videoIdMap }: { videoIdMap: Record<string, string> },
) {
  const regex = /\[video:\s*([\w.]+)]/gim
  return content.replace(regex, (match, capture) => {
    const link = videoIdMap[capture]
    if (!link) return match
    return createAnchor({ link, display: link })
  })
}

export function createImgTagForImageCode(
  imgCode: string,
  attributes?: Record<string, string>,
) {
  const src = `https://kiddom-static.s3.us-east-1.amazonaws.com/images/${imgCode}.jpg`
  return createImgTag({ src, attributes })
}

function swapImageCodes(content: string) {
  const regex = /\[image:\s*([^<>\]]+)]/gim
  return content.replace(regex, (_, capture: string) => {
    const [code, ...rest] = capture.split(',').map((s) => s.trim())
    const attributes: Record<string, string> = {}
    rest.forEach((r) => {
      if (isNaN(Number(r))) {
        attributes.alt = r
      } else {
        attributes.width = r + '%'
      }
    })
    return createImgTagForImageCode(code, attributes)
  })
}

function swapImageSetCodes(content: string) {
  const regex = /\[image-set:\s*([^<>\]]+)]/gm
  return content.replaceAll(regex, (_, capture: string) => {
    const codes = capture.split(',').map((s) => s.trim())
    return codes
      .map((code) => {
        const src = `https://kiddom-static.s3.us-east-1.amazonaws.com/images/${code}.jpg`
        return createImgTag({ src })
      })
      .join('')
  })
}

export function swapContentCodes(
  content: string,
  params: {
    gDrive: Record<string, ParsedGoogleDocType>
    videoIdMap: Record<string, string>
  },
) {
  return [
    swapMaterialCodes,
    swapImageSetCodes,
    swapImageCodes,
    swapVideoCodes,
  ].reduce((acc, swapFunc) => {
    return swapFunc(acc, params)
  }, content)
}

export function isEmptySpaceElement(element: HTMLElement) {
  if (
    element.tagName.toLowerCase() === 'p' &&
    element.childNodes.length === 1 &&
    (element.childNodes.item(0) as HTMLElement).tagName.toLowerCase() ===
      'span' &&
    !element.textContent?.length
  )
    return true
  return element.textContent?.trim() === '[page-break]'
}

export function getUnitLessonIdParts(params: IUnitLessonParams) {
  const [, , unitCode, , lessonCode] = params.metadata['@id'].split('-')
  return { unitCode, lessonCode }
}

export function createActivityForMaterialItem(item: ParsedGoogleDocType) {
  const value = getMaterialDocContent(item.originalDoc)
  const title = getMaterialMetadataForKey(
    'Title',
    item.tableContent,
    stringTransform,
  )
  return createActivity(
    {
      title,
    },
    [createNoteNode({ title, value, studentFacing: false })],
  )
}

export function getMaterialDocContent(doc: Document) {
  const content = Array.from(doc.body.childNodes).reduce<string[]>(
    (acc, child) => {
      if (isEmptySpaceElement(child as HTMLElement)) return acc
      if (
        (child as HTMLElement).tagName.toLowerCase() === 'table' &&
        child.textContent?.startsWith('material-metadata')
      ) {
        return acc
      }
      return [...acc, (child as HTMLElement).outerHTML]
    },
    [],
  )

  return content.join(PARAGRAPH_SPACING)
}

export function convertPhenomTypeToImgElement(
  type: string,
  attributes: Record<string, string>,
  iconMap: Record<string, string>,
) {
  let cleaned = type.toLowerCase().replaceAll('_', '-')
  if (cleaned === 'video' || cleaned === 'video-images') {
    cleaned = 'video-image'
  }
  const src = iconMap[cleaned]
  if (!src) {
    return ''
  }
  return createImgTag({ src, attributes })
}
